// ─── PhoneInput ─────────────────────────────────────────────────────────────
// The phone number box used on the sign-up form (Homepage) and the Signin page.
//
//   ┌──────┬──────────────────────┐
//   │ +65  │ 9123 4567            │
//   └──────┴──────────────────────┘
//
// • "+65" is fixed, so people only type their 8 digits.
// • Only digits get in. Letters and symbols are dropped as they are typed.
// • At most 8 digits. A 9th digit is ignored.
// • Pasting "+65 9123 4567" works: it becomes 9123 4567.
// • A space is shown after the 4th digit so the number is easy to check.
//
// `value` and `onChange` use the plain digits ("91234567"), never the spaces.

import { useState } from 'react';
import { Box } from '@mui/material';

import { GREEN, BORDER, CHARCOAL, MUTED, MUTED_FAINT, bodyFont } from '../../theme/tokens2';
import { cleanPhoneInput, formatPhone } from '../phone';

export default function PhoneInput({
  id,
  value,
  onChange,
  ariaLabel,                     // only needed when there's no <label htmlFor={id}>
  autoComplete = 'tel-national', // asks the browser to fill in the number without +65
  padding = '12px 14px',
  fontSize = '0.9rem',
}) {
  const [focused, setFocused] = useState(false);

  const handleChange = (e) => {
    const digits = cleanPhoneInput(e.target.value);
    if (digits !== null) onChange(digits);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
        boxSizing: 'border-box',
        border: `1px solid ${focused ? GREEN : BORDER}`,
        backgroundColor: '#fff',
        transition: 'border-color 0.2s ease',
        ...bodyFont,
        fontSize,
      }}
    >
      {/* Fixed country code */}
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding,
          borderRight: `1px solid ${BORDER}`,
          color: MUTED,
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        +65
      </Box>

      <Box
        component="input"
        id={id}
        type="tel"
        inputMode="numeric"     // phones show the number keypad
        autoComplete={autoComplete}
        aria-label={ariaLabel}
        placeholder="9123 4567"
        value={formatPhone(value)}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        sx={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          borderRadius: 0,
          padding,
          fontSize: 'inherit',
          fontFamily: 'inherit',
          color: CHARCOAL,
          backgroundColor: 'transparent',
          '&::placeholder': { color: MUTED_FAINT },
        }}
      />
    </Box>
  );
}